import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PrimaryGrid } from '@ui/components/common/Grid';
import SearchFilter from '@ui/components/Button/SearchFilter';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { useParams } from 'react-router-dom';
import { fetchPassportAuditLogsAsync } from '@application/features/familyFest/familyFestPassportThunk';
import { useEffect } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
import { HDatePicker, HTimePicker } from '@ui/components/common/Pickers';
const AuditLogContent = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const [searchValue, setSearchValue] = useState('');
    const [gridFilterOpen, setGridFilterOpen] = useState(false);
    const [filters, setFilters] = useState({});
    const [resetFiltersKey, setResetFiltersKey] = useState(0);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [sortModel, setSortModel] = useState([]);
    const { eventId } = useParams();
    const dispatch = useAppDispatch();
    const { auditLogs } = useAppSelector((state) => state.familyFestPassport);
    useEffect(() => {
        if (!eventId)
            return;
        const sort = sortModel[0];
        dispatch(fetchPassportAuditLogsAsync({
            eventId,
            query: {
                pageNumber: paginationModel.page + 1,
                pageSize: paginationModel.pageSize,
                searchString: searchValue || undefined,
                sortBy: sort?.field,
                sortOrder: sort?.sort ?? 'desc',
                ...filters
            }
        }));
    }, [dispatch, eventId, paginationModel, searchValue, filters, sortModel]);
    const columns = useMemo(() => [
        {
            field: 'username',
            type: 'string',
            headerName: t('events.eventDetails.auditLogTab.username'),
            flex: 1.2,
            minWidth: 150
        },
        {
            field: 'role',
            headerName: t('events.eventDetails.auditLogTab.role'),
            flex: 1,
            minWidth: 120,
            renderCell: (params) => (_jsx(Typography, { variant: 'body2', children: params.row.role?.replace(/([A-Z])/g, ' $1').trim() }))
        },
        {
            field: 'status',
            headerName: t('events.eventDetails.auditLogTab.action'),
            flex: 1.5,
            minWidth: 200,
            renderCell: (params) => {
                const key = params.row.status === 1 ? 'view' : 'export';
                return (_jsx(Typography, { variant: 'body2', children: t(`events.eventDetails.auditLogTab.${key}`) }));
            }
        },
        {
            field: 'timestampUtc',
            headerName: t('events.eventDetails.auditLogTab.timestamp'),
            flex: 1,
            minWidth: 140,
            renderCell: (params) => {
                const date = dayjs.utc(params.row.timestampUtc).local();
                return (_jsx(Typography, { variant: 'body2', children: date.format('MMM D, YYYY HH:mm') }));
            }
        },
        {
            field: 'date',
            headerName: t('events.eventDetails.auditLogTab.date'),
            filterable: true
        },
        {
            field: 'time',
            headerName: t('events.eventDetails.auditLogTab.time'),
            filterable: true
        }
    ], [t]);
    const rows = useMemo(() => auditLogs?.items ?? [], [auditLogs]);
    return (_jsx(Box, { children: _jsx(PrimaryGrid, { columns: columns, rows: rows, isMultiple: false, filterOpen: gridFilterOpen, onFilterOpenChange: setGridFilterOpen, onApplyFilters: (newFilters) => {
                setFilters(newFilters);
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
            }, resetFiltersKey: resetFiltersKey, filterOptions: {
                status: [
                    { label: t('events.eventDetails.auditLogTab.view'), value: 1 },
                    { label: t('events.eventDetails.auditLogTab.export'), value: 2 }
                ]
            }, showHeaderControls: false, showNewHeaderControls: true, paginationModel: paginationModel, onPaginationModelChange: setPaginationModel, paginationMode: 'server', sortingMode: 'server', sortModel: sortModel, onSortModelChange: (model) => {
                setSortModel(model);
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
            }, rowCount: auditLogs?.totalCount ?? 0, pageSizeOptions: [5, 10, 25, 50, 100], maxHeight: 'calc(100dvh - 250px)', initialState: {
                columns: {
                    columnVisibilityModel: {
                        date: false,
                        time: false
                    }
                }
            }, customFilterRender: {
                date: (value, onChange) => (_jsx(HDatePicker, { label: t('events.eventDetails.auditLogTab.date'), value: value, handleSelectedDate: onChange, bg: 'grey8', maxDate: new Date() })),
                time: (value, onChange) => (_jsx(HTimePicker, { label: t('events.eventDetails.auditLogTab.time'), value: value, handleSelectedTime: (newTime) => {
                        if (newTime) {
                            // newTime is "2000-01-01THH:mm:ss.sss" (local)
                            // Convert to UTC to match backend TimestampUtc
                            const localTime = dayjs(newTime);
                            onChange(localTime.toISOString());
                        }
                        else {
                            onChange(null);
                        }
                    }, bg: 'grey8' }))
            }, customButton: _jsxs(Box, { display: 'flex', justifyContent: 'space-between', alignItems: isSmallScreen ? undefined : 'center', flexDirection: isSmallScreen ? 'column' : 'row', gap: isSmallScreen ? '12px' : 0, children: [_jsx(Typography, { variant: 'h4', children: t('events.eventDetails.auditLogTab.auditLogs') }), _jsx(Box, { display: 'flex', alignItems: 'center', gap: 0.5, children: _jsx(SearchFilter, { forceDesktopVariant: true, fullWidth: true, searchValue: searchValue, onSearchChange: (value) => setSearchValue(value), placeholder: t('events.eventDetails.auditLogTab.searchLogs'), onClear: () => setSearchValue(''), showFilterClearButton: Object.keys(filters).length > 0, handleFilterClear: () => {
                                setFilters({});
                                setResetFiltersKey((k) => k + 1);
                            }, widthBox: '220px', isDatagrid: true, iconButtonSize: 'medium', heightBox: '42px', onFilterClick: () => setGridFilterOpen(true), filterLabel: t('users.filters') }) })] }), boxProps: { sx: { display: 'flex', flexDirection: 'column', gap: '12px' } } }) }));
};
export default AuditLogContent;
